import { useMemo } from "react";
import useCompany from "@/contexts/CompanyContext";
import { primaryMenu } from "@/components/layout/Header/shared/nav.constants";
import { generateHeaderNav } from "@/components/layout/Header/shared/nav.utils";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import useApi from "@/contexts/ApiContext";
import useOnrampProjects from "@/hooks/useOnrampProjects";

const useHeaderNavigation = () => {
  const api = useApi();
  const { companyStatus, hasProjectSummary } = useCompany();
  const { user, isAuthenticated } = useAuth0();
  const { data: companySpecificLinks } = useQuery({
    queryKey: ["get-company-links"],
    queryFn: () => api.company.getLinks(),
    staleTime: 30 * (60 * 1000), // 30 mins
    throwOnError: false,
    retry: 3,
  });
  const projectSummaryLink = hasProjectSummary ? "/project-summary" : null;
  const userRoles =
    isAuthenticated && user && user["https://customer.seccl.tech/roles"];
  const canAccessOnramp =
    (companyStatus === "live" || companyStatus === "onboarding") &&
    Array.isArray(userRoles) &&
    userRoles.includes("all_features");

  const { data: onboardingProjects } = useOnrampProjects(canAccessOnramp);

  // Memoize headerNav, only recomputing when the data it relies on changes
  const headerNav = useMemo(() => {
    const navMenu = generateHeaderNav(
      primaryMenu,
      projectSummaryLink,
      userRoles,
      companyStatus,
      companySpecificLinks,
      { onramp: onboardingProjects?.[0]?.project_link },
    );
    return navMenu;
  }, [companySpecificLinks, companyStatus, onboardingProjects, projectSummaryLink, userRoles]);

  return headerNav;
};

export default useHeaderNavigation;
